import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface RevenueSchedule {
  id: string
  contract: string
  total_value: number
  recognized: number
  deferred: number
  recognition_type: string
  start_date: string
  end_date: string
  status: string
}

interface DeferredRevenue {
  id: string
  period: string
  amount: number
  contract_count: number
  avg_contract_value: number
  expected_recognition: number
  risk_amount: number
}

interface RecognitionForecast {
  id: string
  month: string
  expected_recognition: number
  from_existing: number
  from_new: number
  confidence: number
  variance_to_plan: number
}

interface Statistics {
  total_recognized_ytd: number
  total_deferred: number
  recognition_rate: number
  avg_recognition_period: number
  contracts_active: number
  asc606_compliance: number
}

export default function RevenueRecognitionPage() {
  const [schedules, setSchedules] = useState<RevenueSchedule[]>([])
  const [deferred, setDeferred] = useState<DeferredRevenue[]>([])
  const [forecasts, setForecasts] = useState<RecognitionForecast[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [schedulesRes, deferredRes, forecastsRes, statsRes] = await Promise.all([
        fetch('/api/revenuerecognition/schedules'),
        fetch('/api/revenuerecognition/deferred'),
        fetch('/api/revenuerecognition/forecast'),
        fetch('/api/revenuerecognition/statistics')
      ])
      if (schedulesRes.ok) setSchedules(await schedulesRes.json())
      if (deferredRes.ok) setDeferred(await deferredRes.json())
      if (forecastsRes.ok) setForecasts(await forecastsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching revenue recognition data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value)
  }

  const getStatusBadge = (status: string) => {
    if (status === 'active') return 'bg-green-100 text-green-800'
    if (status === 'completed') return 'bg-blue-100 text-blue-800'
    return 'bg-yellow-100 text-yellow-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Revenue Recognition</h1>
          <p className="mt-2 text-gray-600">Phase 237 - Revenue schedules and recognition</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Recognized YTD</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(statistics.total_recognized_ytd)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Deferred</p>
              <p className="text-2xl font-bold text-yellow-600">{formatCurrency(statistics.total_deferred)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Recognition Rate</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.recognition_rate}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Period</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.avg_recognition_period} mo</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Active Contracts</p>
              <p className="text-2xl font-bold text-green-600">{statistics.contracts_active}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">ASC 606 Compliance</p>
              <p className="text-2xl font-bold text-indigo-600">{statistics.asc606_compliance}%</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Revenue Schedules</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contract</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Value</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recognized</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deferred</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {schedules.map((schedule) => (
                  <tr key={schedule.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{schedule.contract}</td>
                    <td className="px-6 py-4 text-gray-500">{formatCurrency(schedule.total_value)}</td>
                    <td className="px-6 py-4 text-green-600">{formatCurrency(schedule.recognized)}</td>
                    <td className="px-6 py-4 text-yellow-600">{formatCurrency(schedule.deferred)}</td>
                    <td className="px-6 py-4 text-gray-500">{schedule.recognition_type}</td>
                    <td className="px-6 py-4 text-gray-500 text-sm">{schedule.start_date} - {schedule.end_date}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(schedule.status)}`}>{schedule.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Deferred Revenue</h2>
            </div>
            <div className="p-6 space-y-4">
              {deferred.map((item) => (
                <div key={item.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{item.period}</span>
                    <span className="text-lg font-bold text-yellow-600">{formatCurrency(item.amount)}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Contracts</p>
                      <p className="font-medium">{item.contract_count}</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Expected</p>
                      <p className="font-medium text-green-600">{formatCurrency(item.expected_recognition)}</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">At Risk</p>
                      <p className="font-medium text-red-600">{formatCurrency(item.risk_amount)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Recognition Forecast</h2>
            </div>
            <div className="p-6 space-y-4">
              {forecasts.map((forecast) => (
                <div key={forecast.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{forecast.month}</span>
                    <span className="text-lg font-bold text-green-600">{formatCurrency(forecast.expected_recognition)}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Existing</p>
                      <p className="font-medium text-blue-600">{formatCurrency(forecast.from_existing)}</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">New</p>
                      <p className="font-medium text-purple-600">{formatCurrency(forecast.from_new)}</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Confidence</p>
                      <p className="font-medium">{forecast.confidence}%</p>
                    </div>
                  </div>
                  <p className={`mt-2 text-sm ${forecast.variance_to_plan >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    Variance: {forecast.variance_to_plan > 0 ? '+' : ''}{forecast.variance_to_plan}%
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
